import { useState } from "react";
import { BirdieGameGroup, User } from "@/types";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import PlayerSelector from "@/components/game/PlayerSelector";

interface EditGameFormProps {
  game: BirdieGameGroup;
  onCancel: () => void;
  onSuccess: () => void;
}

const EditGameForm = ({ game, onCancel, onSuccess }: EditGameFormProps) => {
  const [name, setName] = useState(game.name);
  const [bet, setBet] = useState(game.bet || "");
  const [selectedGolfers, setSelectedGolfers] = useState<User[]>(game.members || []);
  const [isLoading, setIsLoading] = useState(false);

  const handlePlayerSelect = (golfer: User) => {
    if (!selectedGolfers.some(g => g.id === golfer.id)) {
      setSelectedGolfers([...selectedGolfers, golfer]);
    }
  };

  const handlePlayerRemove = (golferId: string) => {
    setSelectedGolfers(selectedGolfers.filter(g => g.id !== golferId));
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast({
        title: "Error",
        description: "Game name is required",
        variant: "destructive"
      });
      return;
    }

    if (selectedGolfers.length === 0) {
      toast({
        title: "Error",
        description: "At least one golfer is required",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);

    try {
      // Update the game
      const { error: gameError } = await supabase
        .from('games')
        .update({
          name,
          bet: bet || null
        })
        .eq('id', game.id);

      if (gameError) throw gameError;

      // Get current players to compare with new selection
      const { data: currentPlayers, error: playersError } = await supabase
        .from('game_players')
        .select('player_id')
        .eq('game_id', game.id);

      if (playersError) throw playersError;

      const currentPlayerIds = currentPlayers.map((p) => p.player_id);
      const newPlayerIds = selectedGolfers.map((g) => g.id);

      // Find players to add and remove
      const playersToAdd = newPlayerIds.filter((id) => !currentPlayerIds.includes(id));
      const playersToRemove = currentPlayerIds.filter((id) => !newPlayerIds.includes(id));

      // Add new players
      if (playersToAdd.length > 0) {
        const { error: addError } = await supabase
          .from('game_players')
          .insert(playersToAdd.map((playerId) => ({
            game_id: game.id,
            player_id: playerId
          })));
        if (addError) throw addError;
      }

      // Remove players that were deselected
      if (playersToRemove.length > 0) {
        for (const playerId of playersToRemove) {
          const { error: removeError } = await supabase
            .from('game_players')
            .delete()
            .eq('game_id', game.id)
            .eq('player_id', playerId);
          if (removeError) throw removeError;
        }
      }

      toast({
        title: "Game Updated",
        description: "Your changes have been saved."
      });
      onSuccess();
    } catch (error) {
      console.error("Error updating game:", error);
      toast({
        title: "Error",
        description: "Failed to update the game. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="border-forest/20">
      <CardContent className="p-4 space-y-4">
        <div className="space-y-2">
          <Label htmlFor="name">Game Name</Label>
          <Input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter game name"
            maxLength={50}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="bet">Bet (optional)</Label>
          <Input
            id="bet"
            value={bet}
            onChange={(e) => setBet(e.target.value)}
            placeholder="e.g. $20 each"
          />
        </div>

        <PlayerSelector
          selectedPlayers={selectedGolfers}
          onPlayerSelect={handlePlayerSelect}
          onPlayerRemove={handlePlayerRemove}
        />

        <div className="flex gap-2 justify-end pt-4">
          <Button variant="outline" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default EditGameForm;
